// AGC: Top KPI summary row — total events, active users, top feature, error rate.
import { useEffect, useState } from 'react'
import { InsightCard } from './InsightCard'
import { getOverviewStats, type DateRange } from '../../../services/analyticsQueryService'
import type { OverviewStats } from '../../../services/analyticsQueryService'
import { interpretOverview } from '../../../services/analyticsInsightGenerator'
import { useTranslation } from '../../../hooks/useTranslation'

interface OverviewSectionProps {
  range: DateRange
}

// AGC: Skeleton placeholder shown while data is loading
function LoadingGrid(): JSX.Element {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[0, 1, 2, 3].map((i) => (
        <InsightCard key={i} title="Loading…">
          <div className="h-10 bg-slate-100 dark:bg-white/[0.04] animate-pulse rounded" />
        </InsightCard>
      ))}
    </div>
  )
}

export function OverviewSection({ range }: OverviewSectionProps): JSX.Element {
  const [stats, setStats] = useState<OverviewStats | null>(null)
  const [loading, setLoading] = useState(true)
  const { t } = useTranslation()

  useEffect(() => {
    // AGC: Cancel flag prevents setState on unmounted component
    let cancelled = false
    setLoading(true)
    getOverviewStats(range)
      .then((s) => { if (!cancelled) setStats(s) })
      .catch((err) => { console.warn('[analytics-dashboard] overview failed', err) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [range])

  if (loading || !stats) {
    return <LoadingGrid />
  }

  // AGC: Error rate color thresholds: >10% red, >5% amber, else emerald
  const errorColor =
    stats.errorRatePct > 10
      ? 'text-red-600 dark:text-red-400'
      : stats.errorRatePct > 5
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-emerald-600 dark:text-emerald-400'

  // AGC: Human-readable period label for the summary card (i18n key lookup)
  const periodLabel = t(`admin.analytics.period.${range}`)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <InsightCard
          title={t('admin.analytics.card.totalEvents')}
          helpTitle="Total events"
          helpDescription="The raw count of every analytics event fired in the selected period."
          helpGoodSignal="Growing week-over-week."
          helpBadSignal="Flat or declining with steady users — users are disengaging."
          helpAction="Pair this with active users to see per-user engagement."
        >
          <div className="text-3xl font-bold text-slate-900 dark:text-slate-100">
            {stats.totalEvents.toLocaleString()}
          </div>
        </InsightCard>

        <InsightCard
          title={t('admin.analytics.card.activeUsers')}
          helpTitle="Active users"
          helpDescription="Unique users who did anything in the selected period."
          helpGoodSignal="Growing weekly."
          helpBadSignal="Fewer active users than installs suggests drop-off after first launch."
          helpAction="If dropping, check the onboarding wizard drop-off section."
        >
          <div className="text-3xl font-bold text-slate-900 dark:text-slate-100">
            {stats.activeUsers.toLocaleString()}
          </div>
        </InsightCard>

        <InsightCard
          title={t('admin.analytics.card.topFeature')}
          helpTitle="Top feature"
          helpDescription="The single feature used the most times in this period."
          helpGoodSignal="One feature dominates — that's your marketing angle."
          helpBadSignal="Everyone uses the same boring screen."
          helpAction="Promote the winner in onboarding."
        >
          <div className="text-2xl font-bold text-slate-900 dark:text-slate-100 truncate">
            {stats.topFeature?.name ?? '—'}
          </div>
          {stats.topFeature && (
            <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {stats.topFeature.count} uses
            </div>
          )}
        </InsightCard>

        <InsightCard
          title={t('admin.analytics.card.errorRate')}
          helpTitle="Error rate"
          helpDescription="Percent of all events that are voice_session_fail or form_submit_fail."
          helpGoodSignal="Under 5%."
          helpBadSignal="Above 10% — users are hitting walls."
          helpAction="Drill into the voice errors and form failures sections below."
        >
          <div className={`text-3xl font-bold ${errorColor}`}>
            {stats.errorRatePct}%
          </div>
        </InsightCard>
      </div>

      {/* AGC: Full-width insight summary card for the whole overview period */}
      <InsightCard title={t('admin.analytics.card.periodSummary')} insight={interpretOverview(stats)}>
        <div className="text-sm text-slate-600 dark:text-slate-300">
          {t('admin.analytics.period.label', { period: periodLabel })}
        </div>
      </InsightCard>
    </div>
  )
}
